import { useCallback, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { useReportCRUD } from "~/hooks/useReportCRUD";
import { handleQueryError } from "~/helpers/errorHandling.helper";
import { useReportDetailStore } from "~/store/details/store";
import { REPORT_DETAIL_STORE_INSTANCE, UpdateFnPromise } from "~/store/details";
import { usePasswordCheck } from "~/hooks/usePasswordCheck";
import { useSessionGuard } from "~/hooks/useSessionGuard";
import { CHECK_SESSION_INTERVAL } from "~/helpers/constants";
import { setGlobalForceSave } from "~/store/details/autoSaveGlobal";
import { reconcileSectionsAfterConflict } from "~/helpers/reconcileReportConflict.helper";
import {
  openReportSyncChannel,
  broadcastReportSaved,
} from "~/helpers/reportTabSync.helper";

/** Quantas vezes um save pode reconciliar+reenviar após 409 antes de desistir
 * e pedir recarga manual — evita loop quando duas abas salvam em rajada. */
const MAX_CONFLICT_RETRIES = 2;

/** Lê o status HTTP de um erro de forma tolerante a shape (axios/fetch). */
function getHttpStatus(error: any): number | undefined {
  return error?.response?.status ?? error?.status;
}

export function useReportActionsWithAutoSave(refetchReport?: () => Promise<any>) {
  // Edição é local até o autosave, e o PUT do save falha com 401 se a sessão morreu.
  const { checkSessionBeforeAction } = useSessionGuard({ interval: CHECK_SESSION_INTERVAL[3] });
  const { withPasswordCheck } = usePasswordCheck();
  const actions = useReportDetailStore((state) => state.actions);
  const { addNewReportMutation,
    setReportDetailsToStale,
    resetReportListQuery
  } = useReportCRUD();

  // Contador de tentativas de reconciliação por ciclo de conflito (#21909).
  const conflictRetriesRef = useRef(0);
  // Ref para handleSaveConflict, evitando ciclo de dependências com executeSave
  // (executeSave chama o conflito no onError; o conflito reagenda executeSave).
  const handleConflictRef = useRef<(reportId: string) => void>(() => {});

  const executeSave = useCallback(() => {
    const state = useReportDetailStore.getState();

    if (!state.autoSave.hasPendingChanges || state.autoSave.isPending) {
      return;
    }

    const { sections, metadata } = state.data;
    const reportId = metadata?.user_reports_id as string;

    const payload = {
      ...metadata,
      data: { [metadata?.report_type as string]: sections },
      // Guard de concorrência otimista (#21909): sinaliza que ESTE save é uma
      // edição manual que deve carregar o `modified_at` base como header. O
      // `modified_at` já viaja em `...metadata`; o flag faz o useReportCRUD
      // promovê-lo a header (fluxos de streaming não marcam o flag).
      __optimisticLock: true,
    };

    useReportDetailStore.setState((state) => ({
      ...state,
      autoSave: {
        ...state.autoSave,
        isPending: true,
        hasPendingChanges: false,
        mutation: addNewReportMutation,
      }
    }));

    addNewReportMutation.mutate(payload, {
      onSuccess: () => {
        conflictRetriesRef.current = 0;

        useReportDetailStore.setState((state) => ({
          ...state,
          autoSave: {
            ...state.autoSave,
            isPending: false,
          }
        }));

        toast.success("Alterações salvas com sucesso!");

        // Sincroniza as outras abas do mesmo relatório (#21909): elas
        // re-hidratam a partir do servidor em vez de salvar por cima.
        if (reportId) {
          const channel = openReportSyncChannel(reportId);
          broadcastReportSaved(channel, reportId, metadata?.modified_at as string | undefined);
          channel?.close();
        }

        withPasswordCheck(() => {
          if (reportId) {
            setReportDetailsToStale(reportId)
            resetReportListQuery()
          };
        });

        // Uma edição pode ter chegado enquanto este save estava em voo
        // (scheduleAutoSave não agenda um segundo em paralelo — ver
        // autoSaveActions.ts). Reagenda aqui, no fim deste, em vez de
        // esperar por um timer independente: mantém no máximo um PUT em
        // voo por vez.
        if (useReportDetailStore.getState().autoSave.hasPendingChanges) {
          executeSave();
        }
      },
      onError: (error) => {
        // Conflito de edição concorrente (#21909): outra aba salvou primeiro.
        // Em vez de descartar a edição local, reconcilia (reaplica as remoções
        // locais sobre a base nova do servidor) e reenvia — bounded, para não
        // entrar em loop.
        if (getHttpStatus(error) === 409 && refetchReport && reportId) {
          handleConflictRef.current(reportId);
          return;
        }

        conflictRetriesRef.current = 0;

        useReportDetailStore.setState((state) => ({
          ...state,
          autoSave: {
            ...state.autoSave,
            isPending: false,
            hasPendingChanges: false,
          }
        }));

        handleQueryError(error, {
          serverErrorMessage: "Falha ao salvar, tente novamente mais tarde.",
          title: "Erro ao salvar",
          onError: () => {
            if (reportId) {
              actions.forceReload();
            }
          }
        });
      },
    });
  }, [addNewReportMutation, actions, withPasswordCheck, setReportDetailsToStale, resetReportListQuery, refetchReport]);

  // Reconcilia um 409 e reenvia (#21909). Puxa a base nova do servidor,
  // reaplica as remoções desta aba por cima e reagenda o save. Após
  // MAX_CONFLICT_RETRIES, para e pede recarga manual — sem nunca perder dado
  // em silêncio.
  const handleSaveConflict = useCallback(async (reportId: string) => {
    if (!refetchReport) return;

    if (conflictRetriesRef.current >= MAX_CONFLICT_RETRIES) {
      conflictRetriesRef.current = 0;
      useReportDetailStore.setState((state) => ({
        ...state,
        autoSave: { ...state.autoSave, isPending: false, hasPendingChanges: false },
      }));
      toast.error(
        "Este relatório está sendo editado em outra aba. Recarregue para ver as alterações mais recentes.",
      );
      actions.forceReload();
      return;
    }

    conflictRetriesRef.current += 1;

    const localSections = useReportDetailStore.getState().data.sections;

    try {
      const result = await refetchReport();
      const serverData = result?.data;
      const serverMap = serverData?.data;
      const typeKey = serverMap ? Object.keys(serverMap)[0] : undefined;
      const serverSections: any[] = (typeKey && serverMap?.[typeKey]) || [];

      // Reaplica as remoções locais sobre a base nova (união das edições).
      const { sections: reconciled } = reconcileSectionsAfterConflict(
        localSections,
        serverSections,
      );

      // Preserva interface_ids para manter accordions abertos, como cancelChanges.
      const withPreservedIds = reconciled.map((section) => {
        const existing = localSections.find(
          (s) => s.title === section.title && s.subsection === section.subsection,
        );
        return { ...section, interface_id: existing?.interface_id || section.interface_id || crypto.randomUUID() };
      });

      REPORT_DETAIL_STORE_INSTANCE.events.setReportSections(withPreservedIds);

      // Atualiza o metadata (inclui o novo modified_at) para o reenvio partir
      // da base correta e não cair em 409 de novo.
      if (serverData) {
        const freshMetadata = { ...serverData };
        delete freshMetadata.data;
        REPORT_DETAIL_STORE_INSTANCE.events.setReportMetadata(freshMetadata as any);
      }

      // Reagenda o save com a base reconciliada.
      useReportDetailStore.setState((state) => ({
        ...state,
        autoSave: { ...state.autoSave, isPending: false, hasPendingChanges: true },
      }));
      executeSave();
    } catch {
      conflictRetriesRef.current = 0;
      useReportDetailStore.setState((state) => ({
        ...state,
        autoSave: { ...state.autoSave, isPending: false, hasPendingChanges: false },
      }));
      toast.error("Não foi possível reconciliar as alterações. Recarregue o relatório e tente novamente.");
      actions.forceReload();
    }
  }, [refetchReport, actions, executeSave]);

  // Mantém a ref apontando para a versão corrente, usada no onError do save.
  useEffect(() => {
    handleConflictRef.current = handleSaveConflict;
  }, [handleSaveConflict]);

  const cancelChanges = useCallback(async () => {
    actions.cancelAutoSave();

    if (refetchReport) {
      const currentState = useReportDetailStore.getState();
      const currentSections = currentState.data.sections;
      const openSectionIds = currentState.ui.openSections;

      const result = await refetchReport();

      if (result.data) {
        const { data: dataMap } = result.data;
        const typeKey = Object.keys(dataMap)[0];
        const foundSections: any[] = dataMap?.[typeKey] || [];

        // Preserva interface_ids para manter accordions abertos
        const sectionsWithPreservedIds = foundSections.map((section) => {
          const existingSection = currentSections.find(
            (s) => s.title === section.title && s.subsection === section.subsection
          );
          return {
            ...section,
            interface_id: existingSection?.interface_id || crypto.randomUUID()
          };
        });

        REPORT_DETAIL_STORE_INSTANCE.events.setReportSections(sectionsWithPreservedIds);
        REPORT_DETAIL_STORE_INSTANCE.events.setOpenSections(openSectionIds);
      }

      toast.info("Alterações descartadas");
    }
  }, [actions, refetchReport]);

  useEffect(() => {
    setGlobalForceSave(executeSave);
    return () => {
      setGlobalForceSave(null);
    };
  }, [executeSave]);

  const updateSectionEntries = useCallback(async (
    sectionTitle: string,
    updaterFn: UpdateFnPromise,
    testEntryDeletedFn: (entry: any) => boolean,
    testSectionDeletedFn: (section: any) => boolean,
    calculateDataCountFn?: (section: any) => number,
    includeSubsections?: boolean,
    crossSectionUpdate?: { matchingProp: string; updaterFn: UpdateFnPromise },
    targetIndex?: number
  ) => {
    // Sinaliza loading antes do await de sessão (que pode ser uma chamada de
    // rede) para que o clique tenha feedback visual imediato, em vez de só
    // aparecer quando `updateSectionEntries` do store roda.
    actions.setActionLoading(true);

    const isSessionValid = await checkSessionBeforeAction();
    if (!isSessionValid) {
      actions.setActionLoading(false);
      return;
    }

    REPORT_DETAIL_STORE_INSTANCE.events.updateSectionEntries(
      sectionTitle,
      updaterFn,
      testEntryDeletedFn,
      testSectionDeletedFn,
      calculateDataCountFn,
      includeSubsections,
      crossSectionUpdate,
      targetIndex
    );

    REPORT_DETAIL_STORE_INSTANCE.events.scheduleAutoSave();
  }, [actions, checkSessionBeforeAction]);

  const updateSubsectionWithMainSection = useCallback(async (
    sectionTitle: string,
    subsectionName: string,
    matchingProp: string,
    updaterFn: UpdateFnPromise,
    testEntryDeletedFn: (entry: any) => boolean,
    testSectionDeletedFn: (section: any) => boolean,
    calculateDataCountFn?: (section: any) => number
  ) => {
    actions.setActionLoading(true);

    const isSessionValid = await checkSessionBeforeAction();
    if (!isSessionValid) {
      actions.setActionLoading(false);
      return;
    }

    REPORT_DETAIL_STORE_INSTANCE.events.updateSubsectionWithMainSection(
      sectionTitle,
      subsectionName,
      matchingProp,
      updaterFn,
      testEntryDeletedFn,
      testSectionDeletedFn,
      calculateDataCountFn
    );

    REPORT_DETAIL_STORE_INSTANCE.events.scheduleAutoSave();
  }, [actions, checkSessionBeforeAction]);

  return useMemo(() => ({
    ...actions,
    updateSectionEntries,
    updateSubsectionWithMainSection,
    forceSave: executeSave,
    cancelChanges,
  }), [actions, updateSectionEntries, updateSubsectionWithMainSection, executeSave, cancelChanges]);
}

